import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  LegendList,
  type LegendListRef,
  type LegendListRenderItemProps,
} from "@-/com.npmjs.legendapp__list";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SurfaceDescription, SurfaceTitle } from "@-/dev.improve.haniverse.components.ui.surface";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import {
  getMorphemeGlyphTileWordGlyphStyle,
  getMorphemeGlyphTileWordPinyinStyle,
  useBatchedWordTileData,
  type GlyphStyle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import runes from "@-/com.npmjs.runes2";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { ToolbarMaterialTabs } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  WordTilesGrid,
  WordDetailsTilesRow,
  WordSectionExpansionButton,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._components.word-tiles-grid";
import { useBrowseTabsContentBottomInset } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.content-bottom-inset";
import {
  BrowseWordsPageContext,
  type WordTileFront,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._context";
import {
  type HskWordGridItem,
  useWordsHskView,
  type WordsHskLevelWords,
} from "#use-words-hsk-view";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { getHskLevelLabel, isHskLevelKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import {
  ActivityIndicator,
  Platform,
  View,
  type LayoutChangeEvent,
} from "@-/com.npmjs.react-native";

const HskLegendList = withUniwind(LegendList<number>);
const WORD_COLUMNS = 8;

export function WordsHskViewContent() {
  const { diameter, height: toolbarHeight } = useToolbarGridMetrics();
  // Reserve the primary toolbar before the material subtoolbar.
  const toolbarPillsBottom = Platform.OS === "web" ? (toolbarHeight + diameter) / 2 : diameter;
  const { availableLevels, hskLevel, levelWords, onHskLevelChange } = useWordsHskView();
  const [windowWidth] = useContextSelectors(RootContext, (c) => c.windowDimensions.width);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    if (width > 0) {
      setContainerWidth(width);
    }
  }, []);
  // Outer p-1, two surface borders, inner px-1 and seven gap-1 spaces.
  const tileSize = Math.max(1, ((containerWidth ?? windowWidth) - 8 - 2 - 8 - 28) / WORD_COLUMNS);

  return (
    <View className="flex-1" onLayout={onLayout} testID="words-hsk-viewport">
      <View className="bg-background" style={{ paddingTop: toolbarPillsBottom + 8 }}>
        <ToolbarMaterialTabs
          onValueChange={onHskLevelChange}
          options={availableLevels.map((level) => ({
            label: `HSK ${getHskLevelLabel(level)}`,
            value: level,
          }))}
          value={hskLevel}
        />
      </View>
      <WordsHskLevelBody hskLevel={hskLevel} levelWords={levelWords} tileSize={tileSize} />
    </View>
  );
}

type WordsHskLevel = ReturnType<typeof useWordsHskView>["hskLevel"];

function WordsHskLevelBody({
  hskLevel,
  levelWords,
  tileSize,
}: {
  hskLevel: WordsHskLevel;
  levelWords: WordsHskLevelWords;
  tileSize: number;
}) {
  switch (levelWords.status) {
    case "pending": {
      return (
        <View className="items-center justify-center px-6 py-12" testID="words-hsk-loading">
          <ActivityIndicator accessibilityLabel="Loading words" />
        </View>
      );
    }
    case "error": {
      return (
        <View className="items-center justify-center gap-3 px-6 py-12" testID="words-hsk-error">
          <Text accessibilityRole="alert" className="text-center" colorVariant="muted">
            Couldn&apos;t load the words in this HSK level.
          </Text>
          <Button onPress={levelWords.retry} variant="outline">
            <Text>Try again</Text>
          </Button>
        </View>
      );
    }
    case "ready": {
      return (
        <WordsHskLevelList
          gridItems={levelWords.gridItems}
          hskLevel={hskLevel}
          tileSize={tileSize}
        />
      );
    }
  }
}

function WordsHskLevelList({
  gridItems,
  hskLevel,
  tileSize,
}: {
  gridItems: readonly HskWordGridItem[];
  hskLevel: WordsHskLevel;
  tileSize: number;
}) {
  const browseTabsContentBottomInset = useBrowseTabsContentBottomInset({ trailingGap: 8 });
  const [expandedLevels, setExpandedLevels] = useState<ReadonlySet<WordsHskLevel>>(() => new Set());
  const expanded = expandedLevels.has(hskLevel);
  const toggleExpanded = useCallback(() => {
    setExpandedLevels((previous) => {
      const next = new Set(previous);
      if (next.has(hskLevel)) {
        next.delete(hskLevel);
      } else {
        next.add(hskLevel);
      }
      return next;
    });
  }, [hskLevel]);
  const columns = expanded ? 4 : WORD_COLUMNS;
  const detailSize = tileSize * 2 + 4;
  const rowHeight = (expanded ? detailSize : tileSize) + 4;
  const wordForms = useMemo(() => gridItems.map((item) => item.simplified), [gridItems]);
  const totalRowsLength = gridItems.length;
  const listRef = useRef<LegendListRef>(null);
  useLayoutEffect(() => {
    // A measured/rotated viewport changes every fixed row size. Retain the scroller,
    // but invalidate its earlier estimated positions before deep scrolling again.
    listRef.current?.clearCaches({ mode: "sizes" });
  }, [rowHeight]);
  const rowStarts = useMemo(
    () => Array.from({ length: Math.ceil(gridItems.length / columns) }, (_, row) => row * columns),
    [columns, gridItems.length],
  );
  const focused = useIsNavigationFocused();
  const push = useBrowseMorphemePageNavigator().push.mutate;
  const onNavigate = useCallback((word: string) => push({ morpheme: word, params: {} }), [push]);
  const [wordTileFront] = useContextSelectors(BrowseWordsPageContext, (c) => c.wordTileFront);
  const getGlyphStyleByCharacterCount = useMemo(() => {
    const styles = new Map<number, GlyphStyle>();
    return (word: string) => {
      const characterCount = runes(word).length;
      let style = styles.get(characterCount);
      if (style === undefined) {
        style = getMorphemeGlyphTileWordGlyphStyle({
          characterCount,
          contentHeight: tileSize,
          contentWidth: tileSize,
        });
        styles.set(characterCount, style);
      }
      return style;
    };
  }, [tileSize]);
  // Numeric row descriptors stay stable when sorting a same-length level. Explicitly
  // invalidate mounted cells for new words/focus/tile-front, while preserving scroll positions.
  const rowContent = useMemo(
    () => ({ expanded, focused, gridItems, rowHeight, wordTileFront }),
    [expanded, focused, gridItems, rowHeight, wordTileFront],
  );
  const renderItem = useCallback(
    ({ item: start }: LegendListRenderItemProps<number>) => (
      <View
        className="border-x border-border/60 bg-surface px-1 pt-1"
        style={{ height: rowHeight }}
        testID="words-hsk-row"
      >
        {focused ? (
          expanded ? (
            <WordDetailsTilesRow
              onNavigate={onNavigate}
              size={detailSize}
              start={start}
              words={wordForms}
            />
          ) : (
            <HskWordRow
              front={wordTileFront}
              getGlyphStyleByCharacterCount={getGlyphStyleByCharacterCount}
              items={gridItems}
              onNavigate={onNavigate}
              start={start}
              tileSize={tileSize}
            />
          )
        ) : null}
      </View>
    ),
    [
      detailSize,
      expanded,
      focused,
      getGlyphStyleByCharacterCount,
      gridItems,
      onNavigate,
      rowHeight,
      tileSize,
      wordTileFront,
      wordForms,
    ],
  );
  const getFixedItemSize = useCallback(() => rowHeight, [rowHeight]);

  return (
    <MorphemeTilePresentationProvider>
      <HskLegendList
        className="flex-1"
        contentContainerClassName="p-1"
        contentContainerStyle={{ paddingBottom: browseTabsContentBottomInset }}
        data={rowStarts}
        drawDistance={150}
        estimatedItemSize={rowHeight}
        extraData={rowContent}
        getFixedItemSize={getFixedItemSize}
        key={hskLevel}
        keyExtractor={String}
        ListEmptyComponent={
          <View className="items-center justify-center px-6 py-12">
            <Text colorVariant="muted">No words in this HSK level.</Text>
          </View>
        }
        ListFooterComponent={
          totalRowsLength === 0 ? null : (
            <View className="mb-2 h-1 rounded-b-xs border-x border-b border-border/60 bg-surface" />
          )
        }
        ListHeaderComponent={
          totalRowsLength === 0 ? null : (
            <View className="flex-row items-center justify-between rounded-t-xs border border-border/60 bg-surface-header px-3 py-2">
              <View className="flex-1">
                <SurfaceTitle>{`HSK ${isHskLevelKey(hskLevel) ? getHskLevelLabel(hskLevel) : ""}`}</SurfaceTitle>
                <SurfaceDescription>{`${totalRowsLength} words`}</SurfaceDescription>
              </View>
              <WordSectionExpansionButton
                expanded={expanded}
                onPress={toggleExpanded}
                section={`HSK ${isHskLevelKey(hskLevel) ? getHskLevelLabel(hskLevel) : ""}`}
              />
            </View>
          )
        }
        recycleItems
        ref={listRef}
        renderItem={renderItem}
        testID="words-hsk-list"
      />
    </MorphemeTilePresentationProvider>
  );
}

function HskWordRow({
  front,
  getGlyphStyleByCharacterCount,
  items,
  onNavigate,
  start,
  tileSize,
}: {
  front: WordTileFront;
  getGlyphStyleByCharacterCount: (word: string) => GlyphStyle;
  items: readonly HskWordGridItem[];
  onNavigate: (word: string) => void;
  start: number;
  tileSize: number;
}) {
  const words = useMemo(() => items.slice(start, start + WORD_COLUMNS), [items, start]);
  const wordForms = useMemo(() => words.map((word) => word.simplified), [words]);
  const data = useBatchedWordTileData(wordForms);
  const getGlyphStyle = useCallback(
    (word: string): GlyphStyle => {
      if (front === "pinyin") {
        return getMorphemeGlyphTileWordPinyinStyle({
          contentHeight: tileSize,
          contentWidth: tileSize,
          toneNumbersPinyinList: data.getSlice(word).primaryPinyin,
        });
      }
      return getGlyphStyleByCharacterCount(word);
    },
    [data, front, getGlyphStyleByCharacterCount, tileSize],
  );
  return (
    <WordTilesGrid front={front} prepared={{ data, getGlyphStyle, onNavigate }} words={words} />
  );
}
